import { useEffect } from 'react';
import { useQueryState } from 'nuqs';
import { flowUrlParams } from '../../../../../../mini-apps/_shared/url-params';
import { useApi } from '../../../lib/api';
import { useSignalsUiStore } from './useSignalsUiStore';

/** The marker the Slack notifier's URL builder appends (`src/services/notifications/action-view/urls.ts`). */
const SLACK_NOTIFICATION_SOURCE = 'slack_notification';

/**
 * Slack "Open in Terra" click attribution (plan 2026-10-13-001 U2, R3). A
 * Slack deep link lands on the Signals tab as
 * `?tab=signals&signal=<id>&source=slack_notification`; the SPA link has no
 * server redirect to hang attribution on, and `usePageTracking` drops the
 * query string, so this POSTs once to
 * `POST /api/flow/signals/:signalId/notification-opened` (which emits
 * "Slack Notification Clicked" with `button: 'open_in_terra'`), then removes
 * `source` from the URL synchronously before the POST is issued (F1), so a
 * POST still in flight can never attribute a row the rep expands next; only
 * the id the model captured at mount as a LINK landing (`landingSignalId`) is
 * ever attributed (R1). A refresh or a shared link never re-attributes. `signal` is left alone (the row stays expanded).
 *
 * StrictMode guard (docs/v4-system/strictmode-resilient-effects.md): the id
 * is recorded in `useSignalsUiStore` synchronously BEFORE the POST, so the
 * double-invoked effect (or a remount) sees it and no-ops. No lifetime ref.
 *
 * Called once from `useSignalTableModel`, which both the V1
 * `SignalsFlowTable` and the V2 `SignalsTable` mount.
 */
export function useSlackClickAttribution(landingSignalId: string | null): void {
  const { apiFetch } = useApi();
  const [source, setSource] = useQueryState('source', { history: 'replace' });
  const [signal] = useQueryState('signal', flowUrlParams.signal.withOptions({ history: 'replace' }));
  const markAttributed = useSignalsUiStore(s => s.markAttributed);

  useEffect(() => {
    if (source !== SLACK_NOTIFICATION_SOURCE || !signal) return;
    // R1/F1 — only the id the page LANDED with is a Slack click. A row the rep
    // expands later (even while the landing POST is still in flight) is not.
    if (!landingSignalId || signal !== landingSignalId) return;
    if (useSignalsUiStore.getState().attributedSignalIds.has(signal)) return;
    markAttributed(signal);
    // F1 — drop `source` NOW, not after the POST settles: a pending POST must
    // never leave `source` armed for the next `signal` value.
    void setSource(null);
    // Best-effort telemetry: a failed POST is not rep-visible and is not retried.
    void apiFetch(`/api/flow/signals/${encodeURIComponent(signal)}/notification-opened`, { method: 'POST' })
      .catch(() => undefined);
  }, [source, signal, landingSignalId, apiFetch, markAttributed, setSource]);
}
